/** @jsxImportSource @emotion/react */
import { useMemo } from 'react';

import { Skeleton, cn } from '@endure/ui';
import { Link } from 'containers/Link';
import { useTranslation } from 'libs/translations';

import { Card, LabeledInlineContent } from 'components';
import { Delimiter } from '../../Delimiter';
import { Select, type SelectOption, type SelectProps } from '../../Select';
import { useStyles } from '../styles';
import type { TableCardProps } from '../types';

export function TableCards<R>({
  cardClassName,
  data,
  isFetching,
  rowKeyExtractor,
  rowOnClick,
  getRowHref,
  breakpoint,
  columns,
  order,
  onOrderChange,
  selectVariant = 'tertiary',
  controls,
  renderRowFooter,
  renderRowControl,
}: TableCardProps<R>) {
  const { t } = useTranslation();
  const styles = useStyles();

  const [titleColumn, ...otherColumns] = columns;

  const isFirstLoad = !!isFetching && data.length === 0;
  const isRefetching = !!isFetching && data.length > 0;

  const selectOptions = useMemo(
    () =>
      columns.reduce((acc, column) => {
        if (!column.sortRows) {
          return acc;
        }

        const option: SelectOption = {
          value: column.key,
          label: column.selectOptionLabel,
        };

        return [...acc, option];
      }, [] as SelectOption[]),
    [columns],
  );

  const selectedOption = useMemo(
    () => order && selectOptions.find((option) => option.value === order.orderBy.key),
    [order, selectOptions],
  );

  const handleOrderChange: SelectProps['onChange'] = (value) => {
    const newSelectedOption = selectOptions.find((option) => option.value === value);
    const orderBy =
      newSelectedOption && columns.find((column) => column.key === newSelectedOption.value);

    if (orderBy) {
      onOrderChange({
        orderBy,
        orderDirection: 'desc',
      });
    }
  };

  if (!titleColumn) {
    return null;
  }

  return (
    <div
      aria-busy={isFetching || undefined}
      className={cn(!breakpoint && 'hidden', breakpoint && `block ${breakpoint}:hidden`)}
    >
      {controls && selectOptions.length > 0 && (
        <Select
          label={t('table.cardsSelect.label')}
          placeLabelToLeft
          options={selectOptions}
          value={selectedOption?.value ?? selectOptions[0]?.value ?? ''}
          onChange={handleOrderChange}
          css={styles.cardsSelect}
          variant={selectVariant}
        />
      )}

      {isFirstLoad && (
        <div className="space-y-6" aria-hidden="true">
          {Array.from({ length: 4 }).map((_, index) => (
            <Card key={`skeleton-card-${index}`} className={cardClassName}>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Skeleton variant="circle" className="h-8 w-8 shrink-0" />
                  <Skeleton className="h-4 w-28" />
                </div>

                <Delimiter />

                {otherColumns.map((column) => (
                  <div key={column.key} className="flex items-center justify-between">
                    <Skeleton className="h-3.5 w-16" />
                    <Skeleton className="h-3.5 w-20" />
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      <div
        className={cn(
          'space-y-6 transition-opacity duration-250',
          isFirstLoad && 'hidden',
          isRefetching && 'opacity-60',
        )}
      >
        {data.map((row, rowIndex) => {
          const rowKey = rowKeyExtractor(row);
          const content = (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>{titleColumn.renderCell(row, rowIndex)}</div>

                {renderRowControl ? renderRowControl(row, rowIndex) : undefined}
              </div>

              {renderRowFooter ? renderRowFooter(row, rowIndex) : undefined}

              <Delimiter />

              {otherColumns.map((column) => (
                <LabeledInlineContent key={`${rowKey}-${column.key}`} label={column.label}>
                  <div className="text-right">{column.renderCell(row, rowIndex)}</div>
                </LabeledInlineContent>
              ))}
            </div>
          );

          return (
            <Card
              key={rowKey}
              className={cn(
                !!(rowOnClick || getRowHref) && 'cursor-pointer hover:bg-cards',
                cardClassName,
              )}
              onClick={rowOnClick && ((e: React.MouseEvent<HTMLDivElement>) => rowOnClick(e, row))}
              asChild
            >
              {getRowHref ? (
                <Link css={styles.link} to={getRowHref(row)}>
                  {content}
                </Link>
              ) : (
                <div>{content}</div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

export default TableCards;
